'use client';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';
import Footer from '@/components/ui/Footer';

const guides = [
  {
    category: 'Home Printers',
    href: '/home-printers',
    color: 'from-blue-600 to-cyan-500',
    icon: '🏠',
    title: 'Home Printer Buying Guide',
    description: 'Find the right refurbished printer for everyday home use — from photo printing to school projects.',
    tips: [
      'Look for Grade A or B refurbished units for best reliability',
      'Inkjet printers are ideal for occasional home use',
      'Check ink cartridge availability and cost before buying',
      'Wi-Fi connectivity is essential for modern home setups',
    ],
  },
  {
    category: 'Office Printers',
    href: '/office-printers',
    color: 'from-violet-600 to-purple-500',
    icon: '🏢',
    title: 'Office Printer Buying Guide',
    description: 'High-volume, reliable printing for small to medium offices — laser and multifunction options explained.',
    tips: [
      'Laser printers offer lower cost-per-page for high volumes',
      'Multifunction printers (print, scan, copy, fax) save desk space',
      'Check monthly duty cycle to match your office workload',
      'Network-ready models allow multiple users to share one device',
    ],
  },
  {
    category: 'Inkjet Printers',
    href: '/inkjet-printers',
    color: 'from-rose-600 to-orange-500',
    icon: '🖨️',
    title: 'Inkjet Printer Tips & Guide',
    description: 'Everything you need to know about inkjet technology, ink types, and getting the best print quality.',
    tips: [
      'Dye-based inks produce vibrant colours; pigment inks last longer',
      'Print regularly to prevent ink nozzle clogging',
      'Use genuine or high-quality compatible cartridges',
      'Check DPI (dots per inch) for photo-quality printing needs',
    ],
  },
];

const faqs = [
  {
    q: 'What does "refurbished" mean?',
    a: 'Refurbished printers have been professionally inspected, cleaned, and tested to ensure they meet performance standards. They may show minor cosmetic wear but function like new.',
  },
  {
    q: 'What are refurbishment grades?',
    a: 'Grade A: Near-mint condition, minimal signs of use. Grade B: Light cosmetic marks, fully functional. Grade C: Visible wear, fully tested and working. All grades are professionally checked.',
  },
  {
    q: 'Are refurbished printers reliable?',
    a: 'Yes. Our refurbished printers undergo rigorous testing. They are a cost-effective and eco-friendly alternative to buying new.',
  },
  {
    q: 'What warranty do refurbished printers come with?',
    a: 'Warranty terms vary by product. Please check the individual product listing for warranty details, or contact our support team.',
  },
];

export default function GuidesPage() {
  return (
    <div className="min-h-screen bg-[#030812] text-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="border-b border-white/10 bg-[linear-gradient(135deg,#07111f_0%,#0f1f3d_100%)] py-16">
          <div className="container mx-auto max-w-6xl px-4">
            <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white">Guides</span>
            </nav>
            <h1 className="text-3xl font-black text-white md:text-4xl">Printer Buying Guides</h1>
            <p className="mt-3 max-w-2xl text-slate-300">
              Expert advice to help you choose the right refurbished printer for your home or office. Understand grades, specs, and what to look for before you buy.
            </p>
          </div>
        </section>

        {/* Guides */}
        <section className="py-14">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid gap-8 md:grid-cols-3">
              {guides?.map((guide) => (
                <div key={guide?.category} className="rounded-2xl border border-white/10 bg-white/[0.04] overflow-hidden">
                  <div className={`bg-gradient-to-r ${guide?.color} p-6`}>
                    <span className="text-4xl">{guide?.icon}</span>
                    <p className="mt-2 text-xs font-bold uppercase tracking-widest text-white/70">{guide?.category}</p>
                    <h2 className="mt-1 text-xl font-black text-white">{guide?.title}</h2>
                  </div>
                  <div className="p-6">
                    <p className="text-sm leading-6 text-slate-300">{guide?.description}</p>
                    <ul className="mt-5 space-y-2.5">
                      {guide?.tips?.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-violet-500/30 text-violet-300 flex items-center justify-center text-xs">✓</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                    <Link
                      href={guide?.href}
                      className="mt-6 inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20"
                    >
                      Browse {guide?.category} →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-white/10 py-14">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 className="text-2xl font-black text-white">Frequently Asked Questions</h2>
            <div className="mt-8 space-y-5">
              {faqs?.map((faq, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                  <h3 className="font-bold text-white">{faq?.q}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{faq?.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
